import { Link, useNavigate, useParams } from 'react-router';
import { DateTime } from 'luxon';
import { ChevronLeft } from 'lucide-react';

import { CampaignDetailsPresenter } from '@/components/campaigns/campaign-details.presenter';
import { PageBreadcrumb } from '@/components/page-breadcrumb';
import { useCampaign } from '@/hooks/use-campaign';
import { useCancelCampaign } from '@/hooks/use-cancel-campaign';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useStoreCampaignAverages } from '@/hooks/use-store-campaign-averages';
import {
  currencyForDisplay,
  useStoreCurrency,
} from '@/hooks/use-store-currency';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import {
  CenteredContent,
  CenteredContentLayout,
  Header,
} from '@/layouts/centered-content.layout';
import { SidebarLayout } from '@/layouts/sidebar.layout';

export default function CampaignDetailsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const campaignId = typeof params.id === 'string' ? params.id : undefined;
  const tenantSlug =
    typeof params.tenantSlug === 'string' ? params.tenantSlug : '';

  const campaign = useCampaign(campaignId ?? '');
  const tenant = useTenant();
  const { LL, locale } = useI18nContext();

  const featureFlags = useFeatureFlags();

  const analyticsEnabled = featureFlags.data?.analyticsEnabled === true;
  const audiencesEnabled = featureFlags.data?.audiencesEnabled === true;

  const isLaunchInFuture =
    !!campaign.data &&
    DateTime.fromISO(campaign.data.launchDateTime).diffNow().as('minutes') > 0;

  const currency = currencyForDisplay(useStoreCurrency());
  // Only a sent campaign with analytics shows Resultados (CAMP-04).
  const storeAverages = useStoreCampaignAverages({
    enabled: analyticsEnabled && !!campaign.data && !isLaunchInFuture,
  });

  const cancelCampaign = useCancelCampaign();
  const handleCancelConfirm = () => {
    if (!campaign.data) return;
    cancelCampaign.mutate(
      { campaignId: campaign.data.id, campaignName: campaign.data.name },
      {
        // 409: it was sent meanwhile, stay on the (refreshed) detail.
        onSuccess: (outcome) => {
          if (outcome !== 'alreadySent') navigate(`/${tenantSlug}/campaigns`);
        },
      }
    );
  };

  if (campaign.isLoading) {
    return (
      <SidebarLayout className="!p-0">
        <CenteredContentLayout>
          <Header className="flex justify-between w-full" rightGutter>
            <div className="flex flex-col gap-2">
              <PageBreadcrumb
                items={[
                  {
                    label: LL.campaigns.title(),
                    href: `/${tenantSlug}/campaigns`,
                  },
                  { label: LL.campaignDetails.breadcrumbLabel() },
                ]}
              />
            </div>
          </Header>
          <CenteredContent>
            <div className="py-10 text-center">
              <p className="text-muted-foreground">
                {LL.campaignDetails.loadingCampaign()}
              </p>
            </div>
          </CenteredContent>
        </CenteredContentLayout>
      </SidebarLayout>
    );
  }

  if (campaign.isError || !campaign.data) {
    return (
      <SidebarLayout className="!p-0">
        <CenteredContentLayout>
          <Header className="flex justify-between w-full" rightGutter>
            <div className="flex flex-col gap-2">
              <PageBreadcrumb
                items={[
                  {
                    label: LL.campaigns.title(),
                    href: `/${tenantSlug}/campaigns`,
                  },
                  { label: LL.campaignDetails.breadcrumbLabel() },
                ]}
              />
              <div className="flex min-w-0 items-center gap-2 sm:gap-4">
                <Link
                  to={`/${tenantSlug}/campaigns`}
                  className="shrink-0"
                  aria-label={LL.campaigns.title()}
                >
                  <ChevronLeft className="size-6" />
                </Link>
              </div>
            </div>
          </Header>
          <CenteredContent>
            <div className="py-10 text-center">
              <p className="text-muted-foreground">
                {LL.campaignDetails.failedToLoadCampaign()}
              </p>
            </div>
          </CenteredContent>
        </CenteredContentLayout>
      </SidebarLayout>
    );
  }

  return (
    <CampaignDetailsPresenter
      campaign={campaign.data}
      tenantSlug={tenantSlug}
      tenantIconUrl={tenant.iconUrl}
      tenantName={tenant.name}
      locale={locale as Locales}
      currency={currency}
      storeAverages={storeAverages}
      analyticsEnabled={analyticsEnabled}
      audiencesEnabled={audiencesEnabled}
      isLaunchInFuture={isLaunchInFuture}
      isCancelling={cancelCampaign.isPending}
      onCancelConfirm={handleCancelConfirm}
    />
  );
}
